import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { GALLERY } from "@/lib/site";
import SectionHeading from "@/components/SectionHeading";

export default function ClinicGallery({ withHeading = true }) {
  const [open, setOpen] = useState(null); // index of active image or null

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open !== null ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const [main, ...rest] = GALLERY;

  return (
    <section className="section" data-testid="clinic-gallery">
      <div className="na-container">
        {withHeading && (
          <SectionHeading
            eyebrow="Our Clinic"
            title="A calm, welcoming space for women's care"
            subtitle="Step inside Nari Arogya — designed to feel warm, private and unhurried, so every consultation is as reassuring as it is thorough."
            align="center"
          />
        )}

        <div className="mt-12 grid gap-4 md:grid-cols-3 md:grid-rows-3 md:h-[780px]">
          {/* Featured image spans full height on desktop */}
          <button
            onClick={() => setOpen(0)}
            className="relative group overflow-hidden rounded-[1.75rem] md:row-span-3 h-64 md:h-full na-card p-0"
            data-testid="gallery-item-0"
          >
            <img
              src={main.src}
              alt={main.caption}
              className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0 opacity-90 group-hover:opacity-100 transition" />
            <span className="absolute left-5 bottom-5 text-white font-display text-xl md:text-2xl drop-shadow-md">
              {main.caption}
            </span>
          </button>

          {rest.map((g, i) => (
            <button
              key={i}
              onClick={() => setOpen(i + 1)}
              className={`relative group overflow-hidden rounded-[1.75rem] h-48 md:h-full na-card p-0 ${g.wide ? "md:col-span-2" : ""}`}
              data-testid={`gallery-item-${i + 1}`}
            >
              <img
                src={g.src}
                alt={g.caption}
                className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0 opacity-80 group-hover:opacity-100 transition" />
              <span className="absolute left-4 bottom-4 text-white font-display text-lg drop-shadow-md">
                {g.caption}
              </span>
            </button>
          ))}
        </div>
      </div>

      {open !== null && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-in fade-in"
          onClick={() => setOpen(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(null); }}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            aria-label="Close"
            data-testid="gallery-close"
          >
            <X size={22} />
          </button>
          <div className="max-w-6xl w-full max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={GALLERY[open].src}
              alt={GALLERY[open].caption}
              className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
            />
            <p className="mt-4 text-white/90 font-display text-lg md:text-xl">{GALLERY[open].caption}</p>
          </div>
        </div>
      )}
    </section>
  );
}
